import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';

import {
  Box,
  Dropdown,
  Flex,
  SiteVariablesPrepared,
} from '@fluentui/react-northstar';
import { Get } from '@microsoft/mgt-react';
import CustomScrollArea from 'react-perfect-scrollbar';
import { separatorStyles } from '../../../../fluent-ui/components/Board/BoardLane';
import MgtCss from '../../../mgt.module.css';
import { getCachedFeature, hasConsentFeature } from '../../../shared/cache/FeatureCache';
import { ThemeColorScheme } from '../../../shared/common/TeamsTheme';
import { LICENSE_FEATURE } from '../../../shared/utils/constants';
import { i18n } from '../../../shared/utils/helper';
import ApprovalsTemplate from '../../WorkBoard/Templates/ApprovalTemplate/ApprovalsTemplate';
import { EmailTemplate } from '../../WorkBoard/Templates/EmailTemplate';
import { TicketTasksTemplate } from '../../WorkBoard/Templates/TicketTasksTemplate/TicketTasksTemplate';
import { TicketTemplate } from '../../WorkBoard/Templates/TicketTemplate';
import { createContentResizeSync } from './laneScrollSync';
import { IMyWorkBoardView } from './MyWorkBoard';

const { t } = i18n();

interface Element {
  title: string;
  template: JSX.Element | JSX.Element[];
  hidden?: boolean;
}

const MY_WORK_TEMPLATES = {
  Tickets: (props: IMyWorkBoardView) => {
    return (<><TicketTemplate {...props } /></>)
  },
  Emails: () => {
    return (
      <Get
        scopes={['mail.read']}
        maxPages={2}
        version='beta'
        resource='/me/mailFolders/inbox/messages?$orderby=createdDateTime desc&$filter=isRead eq false and isDraft eq false'
      >
        <EmailTemplate template='value' />
      </Get>
    );
  },
  TicketTasks: () => <TicketTasksTemplate />,
  Approvals: () => <ApprovalsTemplate />
};

const dropdownDownItems = [
  t('my-work.lane-heading.my-tickets'),
  t('my-work.lane-heading.my-tasks'),
  t('my-work.lane-heading.my-approvals'),
];

const LaneScrollArea: React.FC = ({ children }) => {
  const scrollbar = useRef<CustomScrollArea>(null);
  const observeContent = useMemo(
    () => createContentResizeSync(() => scrollbar.current?.updateScroll()),
    []
  );

  return (
    <CustomScrollArea
      ref={scrollbar}
      style={{ paddingRight: '17px', paddingLeft: '17px', height: 'calc(100% - 51px)', width: '100%', backgroundColor: 'transparent', color: 'var(--mgt-theme-default-foreground)' }}
    >
      {/* Lane columns now divide the row evenly, but cards should keep the
          same width they had under the old fixed 25%-wide (xs={3}) lanes,
          instead of stretching to fill the newly-widened column. Desktop-only
          (see myWorkLaneCard in mgt.module.css) so mobile's single full-width
          lane isn't capped down too. */}
      <div ref={observeContent} className={MgtCss.myWorkLaneCard}>
        {children}
      </div>
    </CustomScrollArea>
  );
};

const MyWorkBoardView: React.FC<IMyWorkBoardView> = (props) => {
  const headerStyles = { fontWeight: 700, fontSize: 'inherit', paddingLeft: 5 };
  const [elements, setElements] = useState<Element[]>([
    {
      title: t('my-work.lane-heading.my-tickets'),
      template: <MY_WORK_TEMPLATES.Tickets {...props} />,
      hidden: false,
    },
    {
      title: t('my-work.lane-heading.my-tasks'),
      template: <MY_WORK_TEMPLATES.TicketTasks />,
      hidden: false,
    },
    {
      title: t('my-work.lane-heading.my-approvals'),
      template: <MY_WORK_TEMPLATES.Approvals />,
      hidden: false,
    },
  ]);
  const laneColSpan = Math.max(1, Math.floor(12 / elements.length));
  const [menu, setMenu] = useState(0);

  useEffect(() => {
    (async () => {
      // MY_WORK's ConsentFeatures entry was retired in #32824 along with the Agenda/Tasks-by-Planner
      // panes it authorized (Calendars.ReadWrite/Group.ReadWrite.All/Mail.Read) - calling it here
      // throws, since ConsentFeatures['MY_WORK'] no longer exists.
      await Promise.all([
        hasConsentFeature('EMAIL_CONNECTOR'),
        getCachedFeature(LICENSE_FEATURE.MultiDepartment),
        getCachedFeature(LICENSE_FEATURE.Collaborators)
      ]);

      setElements([
        {
          title: t('my-work.lane-heading.my-tickets'),
          template: <MY_WORK_TEMPLATES.Tickets {...props} />,
          hidden: false,
        },
        {
          title: t('my-work.lane-heading.my-tasks'),
          template: <MY_WORK_TEMPLATES.TicketTasks />,
          hidden: false,
        },
        {
          title: t('my-work.lane-heading.my-approvals'),
          template: <MY_WORK_TEMPLATES.Approvals />,
          hidden: false,
        },
      ]);
    })();
  }, []);

  return (
    <Container fluid={true} className={`h-full overflow-y-hidden my-work`}>
      <div className={`md:hidden py-3`}>
        <Flex hAlign="center">
          <Dropdown
            className='w-full'
            fluid
            items={dropdownDownItems}
            placeholder={t('my-work.lane-heading.my-tickets')}
            inverted
            onChange={(e, p) => {
              setMenu(p.highlightedIndex);
            }}
          />
        </Flex>
      </div>
      <Row
        className={`${MgtCss.rowMyWork} my-work__board my-work__board--show-${menu}`}
        style={{flexWrap: 'nowrap'}}
      >
        {elements.map((element: Element, index: number) => (
          <Col
            className={`${MgtCss.colMyWork} my-work__board__lane`}
            key={`data_${index}`}
            xs={laneColSpan}
            sm={laneColSpan}
          >
            <div
              className={`hidden-on-mobile--important`}
              style={{
                marginLeft: 10,
                marginTop: 12,
                marginBottom: 15,
                ...headerStyles,
              }}
            >
              {element.title}
            </div>

            <Box
              variables={({ colorScheme }: SiteVariablesPrepared) => ({
                backgroundColor: 'colorScheme.default.background2',
                separatorColor: colorScheme.default.border2,
              })}
              styles={{
                flex: '0 0 auto',
                padding: '0 0 .75rem 0',
                height: 'calc(100vh - 100px) !important',
                ...(index === elements.length - 1 ? {} : separatorStyles),
                ...ThemeColorScheme(props.siteVariables),
              }}
            >
              {!element.hidden && (
                <LaneScrollArea>{element.template}</LaneScrollArea>
              )}
            </Box>
          </Col>
        ))}
      </Row>
    </Container>
  );
};


export default MyWorkBoardView;
